const KEY_BINDINGS = {
  forward: ['KeyW', 'ArrowUp'], // throttle
  back: ['KeyS', 'ArrowDown'], // brake, then reverse
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  handbrake: ['Space', 'ShiftLeft', 'ShiftRight'],
  refuel: ['KeyF'],
  reset: ['KeyR'],
  pause: ['Escape'],
};

/**
 * Everything that a designer is likely to tweak lives here.
 * Game-specific settings can grow into their own modules later — this file is
 * meant to stay a readable overview, not a junk drawer.
 */
export const config = {
  meta: {
    title: 'K-Town Craft',
    stageTitle: 'Street 60',
    eyebrow: 'Khartoum // Sudan // Bashir Elnifidi Street',
    subtitle:
      'One white \u201990 Hilux. Two fuel stations. The longest queue in Khartoum. Survive Bashir Elnifidi Street in a 1:1 recreation of Street 60.',
    pausedTitle: 'Engine off',
    pausedSubtitle:
      'The Hilux is idling beside the fuel queue on Bashir Elnifidi Street, Khartoum. The street waits.',
    // Death screens. `cause` is chosen from the damage source that emptied the
    // health bar ('drone', 'reckless', 'militia', 'rage', 'dshk'), from
    // bankruptcy ('money'), or falls back to 'other' for everything else.
    gameOverRestartNote: 'Press Enter or hit Drive again to restart.',
    deaths: {
      drone: {
        title: 'death by an RSF drone.',
        subtitle: '“what is air defense doing?”',
      },
      reckless: {
        title: 'death by reckless driving.',
        subtitle: '“where did you get your license from?”',
      },
      militia: {
        title: 'death by a militia.',
        subtitle: '“get out of the way civilian!”',
      },
      rage: {
        title: 'death by fuel line disputes.',
        subtitle: '“there is no fuel shortage, right?”',
      },
      dshk: {
        title: 'death by DShK.',
        subtitle: '“are you sure you are not an Infantry formations?”',
      },
      money: {
        title: 'death by inflation',
        subtitle:
          '“If you ain\'t makin\' money, then you ain\'t a money maker, boy”',
      },
      other: {
        title: 'you just died.',
        subtitle: '“who cares.”',
      },
    },
  },

  // Money (Sudanese pounds, SDG). The player starts with a wallet of `start`
  // and the HUD rounds the continuously accumulated value down for display, so
  // the number rises once every two seconds at this rate. If the balance ever
  // reaches zero the run is over — an empty pocket cannot keep the Hilux going.
  money: {
    start: 100,
    pointsPerSecond: 0.5,
  },

  /**
   * The US dollar climbs against the Sudanese pound. Every so often — at a
   * random interval while the player is driving — the dollar goes up and a
   * random slice of the player's SDG savings is wiped out, as if the cash in
   * their pocket suddenly bought less. A toast announces each jump; the money
   * number on the HUD simply drops.
   */
  economy: {
    devaluation: {
      enabled: true,
      intervalMin: 20, // seconds until the earliest jump
      intervalMax: 45, // seconds until the latest jump
      percentMin: 0.05, // fraction of the balance lost per jump
      percentMax: 0.15,
      minLoss: 4, // never bother the player with less than this
      maxLoss: 150, // cap a single jump
    },
  },

  /**
   * Ambient crowd at the fuel stations.
   *
   * A recorded crowd (crowd.mp3) loops around "the gas line" — the long queue
   * of cars waiting for a pump — and swells as the truck comes up on the
   * queue, fading back out as it leaves, so the station announces itself
   * before it comes into view.
   */
  crows: {
    enabled: true,
    /** Metres from the queue at which the crowd starts to be heard. */
    triggerRadius: 150,
    /** Metres from the queue at which it is at full volume. */
    innerRadius: 45,
    /** Level of the crowd bed at full volume. */
    maxVolume: 0.32,
    /** Seconds to fade the crowd in. */
    fadeIn: 1.1,
    /** Seconds to fade it out again. */
    fadeOut: 2.2,
  },

  /**
   * Tea ladies. Stopping beside one buys a jabana: it costs money (SDG) and
   * gives health back. The radius is measured from the stand on the sidewalk,
   * so a truck in the slow lane is inside it.
   */
  tea: {
    price: 50,
    heal: 10,
    radius: 10,
    speedLimit: 1.4, // m/s — the truck has to be stopped
  },

  /**
   * ٥٠ فاكهة, the fruit-juice shop on the west pavement near the north end.
   * Stopping beside it buys a fresh juice using the same one-press health
   * logic as a jabana. The radius covers the whole 18 m frontage and reaches
   * the southbound slow lane, but not the carriageway on the far side.
   */
  juice: {
    price: 50,
    heal: 10,
    radius: 12,
    speedLimit: 1.4, // m/s — the truck has to be stopped
  },

  /**
   * The black-market petrol seller who sets up on the pavement just past each
   * station's exit. He has no pump and no queue: stop beside him, press once,
   * and the tank is full for a flat fee. The station is free but you hold the
   * lever for it; he is expensive but takes one press. The radius is measured
   * from the stall, so the truck has to pull over to the kerb rather than
   * trigger it from the far lane.
   */
  petrol: {
    price: 100,
    radius: 10,
    speedLimit: 1.4, // m/s — the truck has to be stopped
  },

  /**
   * Roadside hitchhikers. Stop within `pickupRadius` of a waving person and
   * they climb into the bed. Drive `minRideDistance` metres, stop again, and
   * they hop out; each completed lift pays `money` SDG.
   */
  hitchhikers: {
    money: 50,
    capacity: 3,
    waveRadius: 32,
    pickupRadius: 8,
    stopSpeed: 1.2, // m/s — considered stopped for pickup/drop-off
    pickupDelay: 0.3,
    minRideDistance: 100,
    dropDelay: 0.75,
    approachSpeed: 5.0,
    abortSpeed: 2.2,
  },

  /** AI rakshas share the civilian fuel queue, then run fares on the return leg. */
  rakshas: {
    speedMultiplier: 1.22,
    accelerationMultiplier: 1.4,
    followGap: 5.2,
    weaveAmplitude: 0.42,
    burnPerKm: 0.16,
    refuelRate: 0.18,
    lowFuelThreshold: 0.22,
    passengerCapacity: 2,
    pickupRadius: 8.5,
    pickupStopTime: 3.2,
    minRideDistance: 180,
    dropDelay: 1.2,
    respawnDelay: 24,
  },

  /**
   * Street 60's minibuses. Four run closed circuits between median openings,
   * pulling to the kerb for anyone who hails them. They hold six passengers, so
   * a single stop can clear half a roadside crowd — and a fare rides far longer
   * than a raksha's, because a bus only sets down where it was always going.
   */
  buses: {
    passengerCapacity: 6,
    pickupRadius: 11,
    pickupStopTime: 5,
    minRideDistance: 600,
    dropDelay: 1.6,
    respawnDelay: 30,
    topSpeed: 10.5,
    minSpeed: 4.5,
  },

  /**
   * Periodic RSF drone raids. A quadcopter makes a five-second pass, launches
   * two rockets at the truck and sends the street into a panic. Rockets track
   * the truck's position at launch, so a moving driver can throw their aim off;
   * a blast within `blastRadius` removes one half of a full health bar.
   */
  droneAttack: {
    firstDelay: 15,
    intervalMin: 28,
    intervalMax: 42,
    duration: 5,
    rocketTimes: [1.1, 2.8],
    rocketFlightTime: 1.55,
    damage: 50,
    blastRadius: 5.5,
    aimSpread: 2.2,
    hoverForward: 28,
    hoverSide: 6,
    hoverHeight: 6.0,
    trafficPanicSpeed: 1.55,
  },

  render: {
    shadows: true,
    maxPixelRatio: 1.75,
    exposure: 1.02,
    fogNear: 60,
    fogFar: 460,
    environmentIntensity: 0.32,
    shadowBounds: 52,
    dunes: false,

    // Cap rendering to the 60 Hz simulation rate. This keeps the truck, camera
    // and physics in phase on 120 Hz displays and avoids adding a physics tick
    // of input-to-visual latency. Set to 0 to render at the display rate, and
    // enable `interpolate` to blend the last two physics poses (smoother on
    // high-refresh displays, at the cost of that one tick of latency).
    maxFps: 60,
    interpolate: false,
  },

  /**
   * Automatic day/night cycle. The world eases through midnight → dawn → noon →
   * dusk on a loop; `cycleSeconds` is one full loop, and `start` is where it
   * begins (0 = midnight, 0.25 = dawn, 0.5 = noon, 0.75 = dusk).
   */
  timeOfDay: {
    cycleSeconds: 480,
    start: 0.22,
  },

  /**
   * Occasional night-only power blackout. The grid fails, every city light goes
   * out, the fog closes to a couple of metres and only the player's own lights
   * still read. It only arms after dark and self-clears after `duration`.
   */
  blackout: {
    enabled: true,
    startGap: 45, // seconds of night before the first one can fire
    cooldownMin: 55, // gap (s) after one clears before the next can arm
    cooldownMax: 120,
    chancePerSecond: 0.04, // probability per second once the cooldown elapses
    duration: 10, // how long the blackout lasts
    // Fog closes in around the truck, not the camera, so the vehicle and its
    // headlights stay visible while only a few metres of road ahead read.
    // Offsets are metres past the camera's follow target (the truck centre).
    fogNearOffset: 2.6,
    fogFarOffset: 6.0,
    exposure: 0.13, // scene goes extremely dark
  },

  camera: {
    fov: 55,
    near: 0.1,
    far: 900,
    sensitivity: 0.0022,
    initialYaw: 0,
    initialPitch: 0.22,
    minPitch: -0.3,
    maxPitch: 1.05,
    distance: 8.2,
    minDistance: 3.2,
    maxDistance: 16,
    zoomSpeed: 0.006,
    targetHeight: 1.15,
    followSpeed: 12,
    collisionPadding: 0.35,
    // Chase behaviour: after a moment without mouse input the camera swings
    // in behind the truck while it is moving.
    alignSpeed: 2.5,
    alignStrength: 3.2,
    alignDelay: 1.1,
    speedDistance: 0.014,
  },

  /**
   * 1990 Toyota Hilux single cab.
   *
   * Distances are in metres, forces in newtons (Bullet's engine/brake values
   * are per wheel). `suspension.connectionHeight` must stay below the chassis
   * box bottom (`chassis[1] / 2`) so the wheel rays never start inside the
   * collider.
   */
  vehicle: {
    mass: 1500,
    // Collider: the low body plus boxes for the cab and bed walls, so the
    // greenhouse cannot slide through overhead geometry. The body origin —
    // and therefore the centre of mass — stays low.
    chassis: [1.7, 0.6, 4.4], // main body collider, full extents
    cabin: [1.6, 0.68, 1.24],
    cabinOffset: [0, 0.51, 0.31],
    bed: [1.6, 0.28, 1.72],
    bedOffset: [0, 0.44, -1.42],
    driven: 'rear', // classic rear-wheel drive
    engineForce: 4200, // total, split across the driven wheels
    reverseForce: 2400,
    brakeForce: 55, // Bullet brake units, per wheel
    handbrakeForce: 160, // locks the rear wheels
    maxSpeed: 26, // m/s ≈ 94 km/h
    maxReverseSpeed: 8,
    downforce: 26, // extra stability at speed
    steerMax: 0.52, // rad at a standstill
    steerSpeedFalloff: 0.055, // steering tightens as speed rises
    steerResponse: 7, // how fast the wheels reach the target angle
    steerSign: 1, // flip to -1 if the truck steers the wrong way
    fallResetY: -30,
    flipResetTime: 2.5,

    // Fuel is stored as a 0..1 tank fraction so the HUD can show a clean
    // gauge. Consumption is distance-based, with a little extra under load.
    // At this scale a full tank is roughly 6 km while working the throttle.
    fuel: {
      startFraction: 0.75,
      burnPerKm: 0.13, // fraction of a tank per kilometre
      loadMultiplier: 1.25,
      lowThreshold: 0.2,
      refuelRate: 0.12, // fraction per second while holding F
      refuelSpeedLimit: 0.8, // m/s; the truck must be essentially stopped
    },

    // Collision health. Every traffic-car impact removes one chunk; the
    // cooldown suppresses double-counting when a rage hit also produces a
    // physical contact on the same frame.
    health: {
      max: 100,
      collisionDamage: 5,
      hitCooldown: 0.5,
      lowThreshold: 0.3,
      // Hood smoke starts below `threshold` and thickens down to zero health.
      smoke: {
        threshold: 0.8,
        maxPuffs: 32,
        rateMin: 1.5,
        rateMax: 14,
        lifeMin: 1.15,
        lifeMax: 2.0,
      },
    },

    // front-left, front-right, rear-left, rear-right
    wheels: [
      { x: 0.78, z: 1.42, front: true },
      { x: -0.78, z: 1.42, front: true },
      { x: 0.78, z: -1.43, front: false },
      { x: -0.78, z: -1.43, front: false },
    ],
    wheel: { radius: 0.37, width: 0.3 },

    // 2009-format Khartoum plate: white 32 × 16 cm, state code KH, bilingual
    // header and numerals. Both bumpers share this content.
    plate: {
      country: 'SUDAN',
      countryArabic: 'السودان',
      seriesArabic: '٧٥',
      code: '7KH',
      numberArabic: '١٠٣٤٦',
      number: '1 0 3 4 6',
    },

    suspension: {
      connectionHeight: -0.33,
      restLength: 0.3,
      stiffness: 25,
      compression: 1.05,
      damping: 1.7,
      travel: 0.34, // metres of suspension travel
      maxForce: 12000,
      frictionSlip: 9.5,
      rollInfluence: 0.09, // lower = less body roll in corners
    },
  },

  physics: {
    gravity: [0, -18, 0],
    fixedStep: 1 / 60,
    maxSubSteps: 4,
  },

  input: {
    bindings: KEY_BINDINGS,
  },

  audio: {
    masterVolume: 0.8,
    // The engine note is synthesized at runtime; these values tune its range
    // and mix without touching the oscillator graph.
    engine: {
      idleRpm: 850,
      maxRpm: 5200,
      idleVolume: 0.035,
      speedVolume: 0.04,
      loadVolume: 0.045,
    },
  },

  colors: {
    sand: '#b39470',
    sandSpeckle: '#9a7d5d',
    grid: 0x6d5b43,
    stone: 0x8f7c63,
    crate: 0xa9713f,
    crateDark: 0x7f522e,
    crateEdge: 0x3d2a18,

    // 1990 Hilux
    carBody: '#f3f1ea',
    carChrome: 0xcfd3d6,
    carBumper: 0x9aa0a6,
    carDark: 0x23262a,
    carSeam: 0x35383c,
    carGlass: 0x27313a,
    carTire: 0x191a1c,
    carHub: 0xb9bfc6,
    carLight: 0xd9e4ea,
    carSignal: 0xd98a2b,
    carBrake: 0xd42a26,
    carReverse: 0xf2f0e6,

    ball: 0x4f8f92,
    skyTop: 0x3d76ad,
    skyHorizon: 0xe8cfa8,
    skyGround: 0xae9570,
    sun: 0xfff1cf,
    fog: 0xd9c2a0,
    dunes: 0xb3946c,
    dust: 0xf2dfbd,
  },

  debug: {
    // Developer diagnostics (FPS and body count). Speed stays on the HUD
    // regardless; flip this to true while profiling.
    showStats: false,
  },
};
